import React from "react";
import {
  Paper,
  Box,
  Avatar,
  Typography,
  Button,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUp from "@mui/icons-material/ThumbUp";
import ThumbDown from "@mui/icons-material/ThumbDown";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RuleIcon from "@mui/icons-material/Rule";
import { Editor } from "@tinymce/tinymce-react";
import IconTextChip from "./IconTextChip";
import IconOnlyChip from "./IconOnlyChip";
import FlagIcon from "@mui/icons-material/Flag";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import ActionReasonDialog from "./ActionReasonDialog";
import { POST_REPORT_REASONS } from "../utils/moderationReasons";
import { reportComment } from "../services/api";
import { isCommentEdited } from "../utils/isEdited";
import EditedTag from "./EditedTag";
import { useInstitution } from "./InstitutionContext";
import { userHasInstitutionRole } from "../utils/permissions";

// Styled list container for nested comments.
const CommentList = styled("ul")(() => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
}));

// Individual comment list item. Each level is indented to align avatars.
const CommentItem = styled("li", {
  shouldForwardProp: (prop) => prop !== "indent",
})(({ theme, indent }) => ({
  paddingLeft: indent,
  listStyle: "none",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

export default function ReplyThread({
  reply,
  depth = 0,
  user,
  verifiedUser = false,
  replyingTo,
  setReplyingTo,
  replyContent,
  setReplyContent,
  onAddReply,
  onEdit,
  onDelete,
  onUpvote,
  onDownvote,
  onLock,
  onRemove,
  onDismiss,
  threadDeleted = false,
  threadLocked = false,
  threadRemoved = false,
  expanded = true,
  onToggle,
  highlightId,
  moderationMode = false,
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [reported, setReported] = React.useState(reply?.reported);
  const [reportOpen, setReportOpen] = React.useState(false);

  const children = Array.isArray(reply?.replies)
    ? [...reply.replies].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      )
    : [];

  const replyCount = Array.isArray(reply?.replies) ? reply.replies.length : 0;

  const { selectedInstitution } = useInstitution();

  if (!reply) return null;

  const effectiveDepth = Math.min(depth, 5);
  const indent = theme.spacing(1 * effectiveDepth);
  const canEdit = user?.id === reply.user?.id;
  const isReplying = replyingTo?.id === reply.id;
  const isDeleted = reply.is_deleted || threadDeleted;
  const isRemoved = reply.is_removed || threadRemoved;
  const isLocked = reply.is_locked || threadLocked;
  const isRestricted = highlightId === reply.id;
  const baseDisabled = isDeleted || isRemoved || isLocked;
  const actionDisabled = baseDisabled || moderationMode || isRestricted;
  const showPlaceholder = isDeleted || isRemoved;
  const canModerate = userHasInstitutionRole(user, 'CommunityAdminTeam', selectedInstitution);

  const handleReport = async (reason) => {
    try {
      await reportComment(reply.id, reason);
      setReported(true);
    } catch (err) {
      console.error("Failed to report comment", err);
    } finally {
      setReportOpen(false);
    }
  };

  return (
    <>
    <CommentList>
      <CommentItem indent={indent}>
        <Avatar
            src={reply.user?.avatar || undefined}
            sx={{ width: 32, height: 32,  }}
          >
            {!reply.user?.avatar && reply.user?.name ? reply.user.name[0] : ""}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              pt: 0.5,
              px: 1.5,
              pb: 0.5,
              bgcolor: "background.paper",
              borderRadius: 1,
              ...(isRestricted && { border: `2px solid ${theme.palette.error.main}` }),
              "&:hover": { backgroundColor: "grey.100" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2"  sx={{ fontWeight: 'bold'}}>
                  {reply.user?.name || ""}
              </Typography>
              {!showPlaceholder && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5}}>
                {reply.created_at
                  ? new Date(reply.created_at).toLocaleString()
                  : ""}
              </Typography>)}
              {!showPlaceholder && (
                <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
                  {user?.permissions?.includes("can_lock_post") &&
                    canModerate &&
                    (!moderationMode || highlightId === reply.id) && (
                      <IconTextChip
                        icon={reply.is_locked ? <LockOpenIcon /> : <LockIcon />}
                        label={reply.is_locked ? "Unlock" : "Lock"}
                        color="warning"
                        onClick={() =>
                          onLock &&
                          (reply.is_locked
                            ? onLock({ id: reply.id, unlock: true })
                            : onLock(reply))
                        }
                        disabled={isRemoved}
                      />
                    )}
                  {user?.permissions?.includes("can_remove_post") &&
                    canModerate &&
                    !reply.is_removed &&
                    (!moderationMode || highlightId === reply.id) && (
                      <IconTextChip
                        icon={<RemoveCircleIcon />}
                        label="Remove"
                        color="error"
                        onClick={() => onRemove && onRemove(reply)}
                        disabled={baseDisabled}
                      />
                    )}
                  {!moderationMode && (
                    <IconTextChip
                      icon={reported ? <FlagIcon /> : <OutlinedFlagIcon />}
                      label={reported ? 'Reported' : 'Report'}
                      color="error"
                      onClick={verifiedUser ? () => setReportOpen(true) : undefined}
                      disabled={reported || !verifiedUser}
                    />
                  )}
                  {moderationMode &&
                    highlightId === reply.id &&
                    onDismiss && (
                      <IconTextChip
                        icon={<RuleIcon />}
                        label="Dismiss Report"
                        color="info"
                        onClick={() => onDismiss(reply.id)}
                      />
                    )}
                </Box>
              )}
            </Box>
            {showPlaceholder ? (
              <Typography
                variant="body2"
                sx={{ fontStyle: "italic", color: "text.secondary" }}
              >
                {isDeleted ? "[deleted]" : "[removed]"}
              </Typography>
            ) : (
              <>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", mb: -1, mt: -1.5, fontSize:'1.1em' }}
                dangerouslySetInnerHTML={{ __html: reply.content }}
              />
              {isCommentEdited(reply) &&
                !['[deleted]', '[removed]'].includes((reply.content || '').trim()) && (
                  <>
                    {' '}
                    <EditedTag />
                  </>
                )}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
              >
                  {verifiedUser && (
                    <IconTextChip
                      icon={<ReplyOutlinedIcon />}
                      label="Reply"
                      color="success"
                      onClick={() => setReplyingTo && setReplyingTo(reply)}
                      disabled={actionDisabled}
                    />
                  )}
                  <IconTextChip
                    icon={reply.upvoted ? <ThumbUp /> : <ThumbUpOffAltIcon />}
                    label={reply.upvotes}
                    color="success"
                    onClick={verifiedUser ? () => onUpvote(reply.id) : undefined}
                    disabled={actionDisabled || !verifiedUser}
                  />
                  <IconTextChip
                    icon={
                      reply.downvoted ? <ThumbDown /> : <ThumbDownOffAltIcon />
                    }
                    label={reply.downvotes}
                    color="success"
                    onClick={verifiedUser ? () => onDownvote(reply.id) : undefined}
                    disabled={actionDisabled || !verifiedUser}
                  />
                  {depth === 0 && replyCount > 0 && (
                    <IconTextChip
                      icon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      label={
                        expanded
                          ? 'Hide All Replies'
                          : 'Show All Replies'
                          // : `Show ${replyCount} ${replyCount === 1 ? 'Reply' : 'Replies'}`
                      }
                      color="primary"
                      onClick={onToggle}
                    />
                  )}
                  <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
                    {verifiedUser && canEdit && (
                      <>
                        <IconOnlyChip
                          icon={<EditOutlinedIcon />}
                          color="success"
                          onClick={() => onEdit && onEdit(reply)}
                          disabled={actionDisabled}
                        />
                        <IconOnlyChip
                          icon={<DeleteOutlineOutlinedIcon />}
                          color="error"
                          onClick={() => onDelete && onDelete(reply)}
                          disabled={actionDisabled}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </>
            )}
            {isReplying && (
              <Box sx={{ mt: 1 }}>
                <Editor
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  value={replyContent}
                  init={{
                    license_key: "gpl",
                    height: 150,
                    menubar: false,
                    plugins: ["link", "lists", "autolink", "charmap", "table"],
                    toolbar:
                      "undo redo | bold italic underline | bullist numlist outdent indent",
                  }}
                  onEditorChange={(content) =>
                    setReplyContent && setReplyContent(content)
                  }
                />
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button
                    onClick={onAddReply}
                    variant="contained"
                    color="primary"
                  >
                    Submit Reply
                  </Button>
                  <Button
                    onClick={() => {
                      setReplyingTo && setReplyingTo(null);
                      setReplyContent && setReplyContent("");
                    }}
                    variant="outlined"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
          {(depth === 0 ? expanded : true) && children.length > 0 && (
            <CommentList>
              {children.map((child) => (
                <ReplyThread
                  key={child.id}
                  reply={child}
                  depth={depth + 1}
                  user={user}
                  verifiedUser={verifiedUser}
                  replyingTo={replyingTo}
                  setReplyingTo={setReplyingTo}
                  replyContent={replyContent}
                  setReplyContent={setReplyContent}
                  onAddReply={onAddReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onUpvote={onUpvote}
                  onDownvote={onDownvote}
                  onLock={onLock}
                  onRemove={onRemove}
                  threadDeleted={threadDeleted}
                  threadLocked={threadLocked}
                  threadRemoved={threadRemoved}
                  highlightId={highlightId}
                  moderationMode={moderationMode}
                  onDismiss={onDismiss}
                />
              ))}
            </CommentList>
          )}
        </Box>
      </CommentItem>
    </CommentList>
    <ActionReasonDialog
      open={reportOpen}
      onClose={() => setReportOpen(false)}
      title="Report Comment"
      reasons={POST_REPORT_REASONS}
      onConfirm={handleReport}
    />
    </>
  );
}
